import {
  use,
  useState,
  createContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { onIdTokenChanged, signOut } from 'firebase/auth'
import { doc as docRef, getDoc } from 'firebase/firestore'
import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import AxiosRequest from '../../../async/axios/AxiosRequest'
import { getCurrentUserProfile } from '../../../async/v1/auth/currentUser'
import Fire from '../../firebaseConfig'
import { useToastAlertContext } from '../../toastAlert/ToastAlertContext'
import {
  PERM_OEM_DEMO_USER,
  PERM_OEM_USER,
} from '../permissions/Permissions.constants'
import { checkUserAccess } from '../permissions/Permissions.utils'
import { CurrentUserError } from './CurrentUserError'
import { useCurrentUserUpdates } from './useCurrentUserUpdates'

const buildPermissionsByRole = (roleDocs) => {
  const permissionsByRole = {}
  roleDocs.forEach((roleDoc) => {
    const data = roleDoc.data()
    permissionsByRole[data.role] = data.permissions
  })
  return permissionsByRole
}

const buildPermissionsByBrandIdOrLocationId = (
  allUserRoles,
  permissionsByRole,
) => {
  const permissionsByBrandIdOrLocationId = {}
  Object.keys(allUserRoles).forEach((brandId) => {
    permissionsByBrandIdOrLocationId[brandId] =
      permissionsByRole[allUserRoles[brandId]]
  })
  return permissionsByBrandIdOrLocationId
}

export const UserContext = createContext({})
export const UserContextProvider = ({ children }) => {
  const [authReady, setAuthReady] = useState(false)
  const [authUser, setAuthUser] = useState({})
  const [profileState, setProfileState] = useState({})
  const [attempt, setAttempt] = useState(0)
  const { showAxiosAlert } = useToastAlertContext()
  const uid = authUser?.uid
  const profile = profileState.uid === uid ? profileState.data : undefined
  const error = profileState.uid === uid ? profileState.error : undefined
  const accessDenied = profileState.uid === uid && profileState.accessDenied
  const refresh = useCallback(() => setAttempt((current) => current + 1), [])
  const { error: updatesError, retry: retryUpdates } = useCurrentUserUpdates(
    uid,
    refresh,
  )
  const retry = useCallback(() => {
    refresh()
    if (updatesError) retryUpdates()
  }, [refresh, retryUpdates, updatesError])

  useEffect(
    () =>
      onIdTokenChanged(Fire.Auth, (incomingAuthUser) => {
        AxiosRequest.setUserToken(incomingAuthUser || {})
        setAuthUser(incomingAuthUser || {})
        setAuthReady(true)
      }),
    [],
  )

  useEffect(() => {
    if (!uid) {
      setProfileState({})
      return
    }
    const controller = new AbortController()
    setProfileState((previous) => ({
      uid,
      data: previous.uid === uid ? previous.data : undefined,
      permissions: previous.uid === uid ? previous.permissions : undefined,
    }))
    getCurrentUserProfile(controller.signal)
      .then(async ({ data }) => {
        const allUserRoles = { ...data.oemRoles, ...data.roles }
        const uniqueRoles = [...new Set(Object.values(allUserRoles))]
        const roleDocs = await Promise.all(
          uniqueRoles.map((role) => getDoc(docRef(Fire.DB, 'roles', role))),
        )
        const permissionsByRole = buildPermissionsByRole(roleDocs)
        const userPermissions = buildPermissionsByBrandIdOrLocationId(
          allUserRoles,
          permissionsByRole,
        )
        if (!controller.signal.aborted) {
          setProfileState((previous) => ({
            uid,
            data: isEqual(previous.data, data) ? previous.data : data,
            permissions: isEqual(previous.permissions, userPermissions)
              ? previous.permissions
              : userPermissions,
          }))
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          if (requestError.response?.status === 403) {
            setProfileState({ uid, accessDenied: true })
          } else {
            setProfileState((previous) => ({
              ...previous,
              error: requestError,
            }))
          }
        }
      })
    return () => controller.abort()
  }, [uid, attempt])

  useEffect(() => {
    if (profile && (error || updatesError)) {
      showAxiosAlert(updatesError || 'Unable to refresh your account.', {
        error,
        toastOptions: {
          primaryButtonLabel: 'Retry',
          onPrimaryButtonClicked: retry,
        },
      })
    }
  }, [profile, error, updatesError, showAxiosAlert, retry])

  const permissions =
    profileState.uid === uid ? profileState.permissions || {} : {}
  const userHasOemAccess = checkUserAccess(permissions, [
    PERM_OEM_USER,
    PERM_OEM_DEMO_USER,
  ])
  const isLoggedIn = !!(uid && (profile || accessDenied))
  const loadingUser =
    !authReady || !!(uid && !profile && !error && !accessDenied)
  const context = useMemo(
    () => ({
      isLoggedIn,
      loadingUser,
      authUser,
      user: profile || (accessDenied ? { userName: authUser.email } : {}),
      permissions,
      userHasOemAccess,
    }),
    [
      isLoggedIn,
      loadingUser,
      authUser,
      profile,
      accessDenied,
      permissions,
      userHasOemAccess,
    ],
  )
  const handleSignOut = () => {
    signOut(Fire.Auth).catch((signOutError) =>
      showAxiosAlert('Unable to sign out.', { error: signOutError }),
    )
  }

  return (
    <UserContext value={context}>
      {uid && error && !profile ? (
        <CurrentUserError onRetry={retry} onSignOut={handleSignOut} />
      ) : (
        children
      )}
    </UserContext>
  )
}

export const useUserContext = () => use(UserContext)

UserContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf([PropTypes.node]),
  ]).isRequired,
}
